import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { tapHaptic } from '../../shared/lib/haptics';
import { Button } from '../../shared/ui/Button';
import { Label } from '../../shared/ui/Label';
import { PageHeader } from '../../shared/ui/PageHeader';
import { Skeleton } from '../../shared/ui/Skeleton';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { useToast } from '../../shared/ui/toast-context';

import {
  ADMIN_QUERY_KEYS,
  approveInstitute,
  listPendingInstitutes,
  rejectInstitute,
  type PendingInstituteRow,
} from './api';
import s from './InstitutesQueuePage.module.css';

function initials(value: string): string {
  const parts = value.split(/[@\s.-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.max(1, Math.round(ms / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

const SKELETON_ROWS = [0, 1, 2] as const;

/** Mirrors `.row` — avatar tile, name + meta, two facts, Reject/Approve. */
function ApplicationSkeleton(): JSX.Element {
  return (
    <li className={s.row}>
      <Skeleton width={44} height={44} radius="md" />
      <div className={s.main}>
        <div className={s.mainHead}>
          <Skeleton width="50%" height={20} />
          <Skeleton width="35%" height={14} />
        </div>
        <div className={s.facts}>
          <Skeleton width="70%" height={32} radius="sm" />
          <Skeleton width="60%" height={32} radius="sm" />
        </div>
      </div>
      <div className={s.actions}>
        <Skeleton width={72} height={40} radius="md" />
        <Skeleton width={96} height={40} radius="md" />
      </div>
    </li>
  );
}

export function AdminInstitutesQueuePage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [serverError, setServerError] = useState<string | null>(null);
  const [pendingApprovalId, setPendingApprovalId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingInstituteRow | null>(null);

  const list = useQuery({
    queryKey: ADMIN_QUERY_KEYS.pendingInstitutes,
    queryFn: listPendingInstitutes,
  });

  const approve = useMutation({
    mutationFn: approveInstitute,
    onMutate: (id: string) => {
      setPendingApprovalId(id);
      setServerError(null);
    },
    onSuccess: (_, id) => {
      const row = list.data?.find((r) => r.id === id);
      toast.show(`Approved "${row?.name ?? id}". The owner can now sign in.`, {
        tone: 'success',
      });
      tapHaptic();
      void queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingInstitutes });
      void queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.institutes });
    },
    onError: (err: Error) => setServerError(err.message),
    onSettled: () => setPendingApprovalId(null),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectInstitute(id, reason),
    onSuccess: (_, vars) => {
      const row = list.data?.find((r) => r.id === vars.id);
      toast.show(`Rejected "${row?.name ?? vars.id}". The owner was notified.`, {
        tone: 'success',
      });
      tapHaptic();
      setRejectTarget(null);
      void queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingInstitutes });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const rows = list.data ?? [];

  return (
    <div className={s.root}>
      <PageHeader
        eyebrow="Approvals"
        title="Pending institutes"
        subtitle="Review self-service applications. Approving sends the owner into their dashboard; rejecting deletes the application and notifies the owner with your reason."
        actions={
          <span className={s.countPill} aria-live="polite">
            {rows.length} pending
          </span>
        }
      />

      {serverError !== null ? (
        <div className={s.banner}>
          <StatusBanner tone="error">{serverError}</StatusBanner>
        </div>
      ) : null}

      <section className={s.section}>
        {list.isPending ? (
          <ul className={s.list} role="status" aria-label="Loading pending applications">
            {SKELETON_ROWS.map((row) => (
              <ApplicationSkeleton key={row} />
            ))}
          </ul>
        ) : list.error !== null ? (
          <StatusBanner tone="error">
            Could not load pending applications: {list.error.message}
          </StatusBanner>
        ) : rows.length === 0 ? (
          <div className={s.empty}>
            <span className={s.emptyIcon} aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4.5 6.5h13M4.5 11h13M4.5 15.5h8" />
              </svg>
            </span>
            <h2 className={s.emptyTitle}>No pending applications</h2>
            <p className={s.emptyBody}>
              When an institute fills out <code>/register-institute</code>, their request appears
              here for approval.
            </p>
          </div>
        ) : (
          <ul className={s.list}>
            {rows.map((row) => {
              const isApproving = pendingApprovalId === row.id;
              return (
                <li key={row.id} className={s.row}>
                  <span className={s.avatar} aria-hidden="true">
                    {initials(row.name)}
                  </span>
                  <div className={s.main}>
                    <div className={s.mainHead}>
                      <span className={s.rowName}>{row.name}</span>
                      <span className={s.rowMeta}>
                        <code>{row.slug}</code> · submitted {timeAgo(row.created_at)}
                      </span>
                    </div>
                    <dl className={s.facts}>
                      <div>
                        <dt>Owner email</dt>
                        <dd>{row.owner_email}</dd>
                      </div>
                      {row.owner_phone !== null && row.owner_phone !== '' ? (
                        <div>
                          <dt>Owner phone</dt>
                          <dd>{row.owner_phone}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                  <div className={s.actions}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setRejectTarget(row)}
                      disabled={approve.isPending || reject.isPending}
                    >
                      Reject
                    </Button>
                    <Button
                      type="button"
                      onClick={() => approve.mutate(row.id)}
                      loading={isApproving}
                      disabled={(approve.isPending && !isApproving) || reject.isPending}
                    >
                      Approve
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <RejectModal
        target={rejectTarget}
        loading={reject.isPending}
        onCancel={() => setRejectTarget(null)}
        onConfirm={(reason) =>
          rejectTarget !== null ? reject.mutate({ id: rejectTarget.id, reason }) : undefined
        }
      />
    </div>
  );
}

interface RejectModalProps {
  target: PendingInstituteRow | null;
  loading: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

function RejectModal({
  target,
  loading,
  onCancel,
  onConfirm,
}: RejectModalProps): JSX.Element | null {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (target === null) {
      setReason('');
      setTouched(false);
      return undefined;
    }
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target, onCancel]);

  if (target === null) return null;

  const trimmed = reason.trim();
  const tooShort = trimmed.length < 5;
  const showError = touched && tooShort;

  return (
    <div className={s.backdrop} role="dialog" aria-modal="true" aria-labelledby="reject-title">
      <div className={s.modal}>
        <h2 id="reject-title" className={s.modalTitle}>
          Reject &ldquo;{target.name}&rdquo;?
        </h2>
        <p className={s.modalBody}>
          The application will be removed and the owner will receive a notification with your
          reason. This cannot be undone — they would need to reapply.
        </p>
        <div className={s.modalField}>
          <Label htmlFor="reject-reason">Reason for rejection</Label>
          <textarea
            id="reject-reason"
            className={s.textarea}
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={loading}
            aria-invalid={showError}
            aria-describedby={showError ? 'reject-reason-error' : undefined}
            placeholder="Visible to the institute owner"
          />
          {showError ? (
            <p id="reject-reason-error" className={s.fieldError}>
              Reason must be at least 5 characters.
            </p>
          ) : null}
        </div>
        <div className={s.modalActions}>
          <Button
            type="button"
            variant="ghost"
            ref={cancelRef}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => {
              setTouched(true);
              if (!tooShort) onConfirm(trimmed);
            }}
            loading={loading}
            disabled={tooShort}
          >
            Reject application
          </Button>
        </div>
      </div>
    </div>
  );
}
